"use client";

import { motion } from "framer-motion";
import {
  ArrowRight,
  MessageSquare,
  Cpu,
  FileOutput,
  CandlestickChart,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { track } from "@/components/analytics/track";

const stepDefs: {
  icon: LucideIcon;
  titleKey: "step1Title" | "step2Title" | "step3Title" | "step4Title";
  descKey: "step1Desc" | "step2Desc" | "step3Desc" | "step4Desc";
  pro?: boolean;
}[] = [
  { icon: MessageSquare, titleKey: "step1Title", descKey: "step1Desc" },
  { icon: Cpu, titleKey: "step2Title", descKey: "step2Desc" },
  { icon: FileOutput, titleKey: "step3Title", descKey: "step3Desc" },
  {
    icon: CandlestickChart,
    titleKey: "step4Title",
    descKey: "step4Desc",
    pro: true,
  },
];

/**
 * Closed-loop pipeline: idea → engine → file → Terminal / Pro live.
 */
export function HowItWorks() {
  const t = useTranslations("Home");

  return (
    <section id="how-it-works" className="relative px-4 py-24 quant-grid sm:px-6">
      <div className="relative z-10 mx-auto w-full max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="mb-16 text-center"
        >
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-teal-light">
            {t("howEyebrow")}
          </p>
          <h2 className="mx-auto mb-4 max-w-[20.5rem] text-[clamp(1.5rem,6vw,3rem)] font-bold tracking-tight text-pretty text-slate-100 sm:max-w-none">
            {t("howTitle")}
          </h2>
          <p className="mx-auto w-full max-w-[20.5rem] text-base leading-relaxed text-slate-400 sm:max-w-2xl sm:text-lg">
            {t("howIntro")}
          </p>
        </motion.div>

        <div className="grid min-w-0 grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
          {stepDefs.map((step, index) => (
            <motion.article
              key={step.titleKey}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.45, delay: index * 0.08 }}
              className={`relative flex min-w-0 flex-col rounded-2xl border p-6 md:p-7 ${
                step.pro
                  ? "border-maroon/45 bg-gradient-to-b from-maroon/15 to-slate-900/80 shadow-[0_0_40px_-18px_rgba(155,58,58,0.55)]"
                  : "border-slate-700/60 bg-slate-900/50"
              }`}
            >
              <div className="mb-5 flex items-center justify-between">
                <span className="font-mono text-sm text-teal-light/80">
                  {t("stepLabel", { n: String(index + 1).padStart(2, "0") })}
                </span>
                {step.pro ? (
                  <span className="rounded bg-maroon px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white">
                    {t("step4Badge")}
                  </span>
                ) : (
                  <span
                    className="h-2 w-2 rounded-full bg-teal-light/40"
                    aria-hidden
                  />
                )}
              </div>
              <step.icon
                className={`mb-4 h-7 w-7 ${step.pro ? "text-maroon" : "text-teal-light"}`}
                aria-hidden
              />
              <h3 className="mb-3 text-xl font-semibold leading-snug text-slate-100">
                {t(step.titleKey)}
              </h3>
              <p className="w-full min-w-0 break-words text-sm leading-relaxed text-pretty text-slate-400">
                {t(step.descKey)}
              </p>
            </motion.article>
          ))}
        </div>

        <div className="mt-14 flex justify-center">
          <Link
            href="/product"
            onClick={() =>
              track("cta_click", { meta: { label: "how_download" } })
            }
            className="group inline-flex items-center gap-2 rounded-lg bg-maroon px-8 py-4 font-semibold text-white shadow-lg shadow-maroon/25 transition-all duration-200 hover:bg-maroon-hover"
          >
            {t("downloadCta")}
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
}
